// Static-wording translations for the payment-link customer flow.
// Only covers UI chrome hardcoded in the frontend — never text sourced from API
// responses (merchant/customer names, amounts, item names, backend error messages).
const translations = {
  en: {
    languageNames: {
      en: "English",
      id: "Bahasa Indonesia",
      zh: "中文",
    },
    layout: {
      payBefore: "Pay before",
      dateAt: "at",
      back: "Back",
      poweredBy: "Powered by",
      poweredBySuffix: "",
    },
    common: {
      name: "Name",
      phoneNumber: "Phone Number",
      email: "Email",
      checkPaymentStatus: "Check Payment Status",
      processing: "Processing...",
      close: "Close",
    },
    paymentMethod: {
      title: "Choose Payment Methods",
      categoryVa: "Virtual Account",
      categoryCard: "Credit / Debit Card",
      categoryEwallet: "E-Wallet",
      categoryQris: "QRIS",
      categoryOfflineStore: "Retail Outlet",
      categoryDirectDebit: "Direct Debit",
      qrisSupportNote: "Supports QRIS from major banks and popular e-wallets",
      generatingQris: "Generating QRIS...",
      qrisNotAvailable: "QRIS is not available yet.",
      downloadQris: "Download QRIS",
      cardNumberLabel: "Card number",
      cardExpiryLabel: "Expiry date",
      cvvLabel: "CVV",
      nameLabel: "Name",
      emailLabel: "Email",
      billingAddressLabel: "Billing Address",
      payNow: "Pay Now",
      errorCardNumberInvalid: "Please enter a valid card number.",
      errorCardNotSupported: "We only accept Visa, Mastercard, JCB, and American Express.",
      errorExpiryInvalid: "Please enter a valid expiry date.",
      errorCvvDigits4: "Please enter 4 CVV digits.",
      errorCvvDigits3: "Please enter 3 CVV digits.",
      errorCardHolderRequired: "Please enter the cardholder name.",
      errorEmailInvalid: "Please enter a valid email address.",
    },
    paymentDetail: {
      yourDetails: "Your details",
      inputName: "Input name",
      inputPhoneNumber: "Input phone number",
      inputEmail: "Input email",
      continue: "Continue",
      paymentDetailTitle: "Payment Detail",
      to: "To",
      items: "Items",
      totalAmount: "Total Amount",
      customerDetail: "Customer Detail",
      next: "Next",
    },
    customerDetailForm: {
      fullName: "Full Name",
      phone: "Phone",
      johnDoePlaceholder: "john doe",
      phonePlaceholder: "08xxxxxxxxxx",
      phoneHelper: "This number will receive invoice and payment notifications",
      emailPlaceholder: "johndoe@gmail.com",
      emailHelper: "This email will receive invoice and payment notifications",
    },
    secondForm: {
      loadingMethods: "Loading payment methods...",
      processingPayment: "Processing payment...",
      preparingSession: "Preparing payment session...",
      allowNotifications: "Please allow notifications and refresh this page.",
      sessionNotReady: "Payment session is not ready.",
      failedLoadMethods: "Failed to load payment methods",
      notifErrorTitle: "Payment Processing Failed",
      notifErrorGenericMessage: "An error occurred while processing the payment. Please try again.",
      preparingSessionNotifTitle: "Preparing Payment Session",
      preparingSessionNotifMessage: "Please wait until the payment session is ready.",
      statusNotifTitle: "Transaction Status",
      paymentReceivedMessage: "Payment has been received",
      statusIsPrefix: "This transaction status is",
      paymentNotifTitle: "Payment Notification",
      failedCardSession: "Failed to create card payment session.",
      cardPaymentFailedFallback: "Card payment failed.",
      threeDsChallengeMissing: "3DS verification required but no challenge was returned. Please try again.",
      creditDebitCard: "Credit / Debit Card",
    },
    thirdForm: {
      loading: "Loading...",
      statusNotifTitle: "Transaction Status",
      paymentReceivedMessage: "Payment has been received",
      statusIsPrefix: "This transaction status is",
      paymentNotifTitle: "Payment Notification",
      errorTitle: "Error",
      errorCheckStatus: "Failed to check transaction status.",
      unknownMethod: "Payment method not recognized",
    },
    errorTransactionNotFound: {
      fulfilledTitle: "Payment Link Has Been Fully Paid",
      fulfilledMessage:
        "All installments on this payment link have been successfully paid in full. No further action is required from you.",
      expiredTitle: "Payment Link Expired",
      expiredMessage:
        "This payment link has expired and can no longer accept payments. If you still need to complete this payment, please contact the merchant for a new link.",
      paidTitle: "Payment Already Completed",
      paidMessage: "This payment has already been completed successfully. No further action is required.",
      sessionLostTitle: "Session Expired",
      sessionLostMessage: "Your session has expired. Please reload the page or reopen the payment link to start a new session.",
      defaultTitle: "Transaction Not Found",
      defaultMessage: "We couldn't find the transaction you're looking for. It may have been closed, fulfilled, or expired.",
    },
    paymentSuccess: {
      title: "Payment Successful",
      orderPaidPrefix: "Your order #",
      orderPaidSuffix: "has been paid for successfully",
      amountPaid: "Amount Paid",
      datePaid: "Date Paid",
      paymentMethod: "Payment Method",
    },
    paymentLinkExpired: {
      title: "Payment Link Expired",
      message:
        "This payment link has expired and can no longer accept payments. If you still need to complete this payment, please contact the merchant for a new link",
    },
    paymentInstructionVA: {
      vaNumber: "Virtual Account Number",
      vaName: "Virtual Account Name",
      instructionsSuffix: "Instructions",
      yourVaNumberFallback: "your Virtual Account Number",
    },
    paymentInstructionQRIS: {
      pleaseScan: "Please scan",
      howToPay: "How to Pay",
      stepByStep: "Step-by-step Instructions",
      instructions: [
        "Scan or screenshot the QR code",
        "Open your bank/e-wallet app (Gojek, OVO, Dana, QRIS BCA, etc.).",
        "Select 'Pay' or 'Scan'",
        "Upload the QR code screenshot.",
        "Enter your e-wallet PIN.",
        "Once the payment is complete and successful, you'll receive a notification.",
      ],
    },
    paymentInstructionEwallet: {
      payWith: "Pay With",
      sections: {
        EWALLET_SHOPEEPAY: [
          {
            id: "app",
            title: "Shopee App Instructions",
            content: [
              "Tap the Payment Button - Select 'Pay with ShopeePay' above.",
              "Open the App - You'll be automatically directed to the Shopee app.",
              "Verify Order - Check the total bill and merchant name.",
              "Choose Method - Use Balance, SPayLater, or SeaBank Direct Debit.",
              "Confirm PIN - Input your ShopeePay PIN to complete.",
              "Done - The payment status will be updated automatically when you return to this page.",
            ],
          },
          {
            id: "web",
            title: "ShopeePay Website Instructions",
            content: [
              "Tap the Payment Button - Select 'Pay with ShopeePay' above.",
              "Open the ShopeePay page in your browser.",
              "Verify Order - Check the total bill and merchant name.",
              "Choose an available ShopeePay payment method.",
              "Confirm PIN - Input your ShopeePay PIN to complete.",
              "Done - The payment status will be updated automatically when you return to this page.",
            ],
          },
        ],
        EWALLET_DANA: [
          {
            id: "app",
            title: "Dana App",
            content: [
              "1. Tap the 'Pay with Dana' button above",
              "2. You'll be redirected to the Dana app",
              "3. Log in with your phone number if prompted",
              "4. Check the payment details (amount and merchant)",
              "5. Choose an available payment method in Dana:",
              "   - Dana Balance",
              "   - Debit/Credit Card (if available)",
              "   - Other payment methods",
              "6. Enter your 6-digit Dana PIN",
              "7. Wait for payment confirmation",
              "8. You'll automatically return to this page",
              "9. The payment status will update automatically",
            ],
          },
          {
            id: "web",
            title: "Dana Website",
            content: [
              "1. Tap the 'Pay with Dana' button above",
              "2. You'll be redirected to the Dana website",
              "3. Log in with your phone number and OTP",
              "4. Check the payment details",
              "5. Choose an available payment method",
              "6. Enter your Dana PIN",
              "7. Wait for payment confirmation",
              "8. Return to this page to see the status",
            ],
          },
        ],
        EWALLET_GOPAY: [
          {
            id: "app",
            title: "Gojek / GoPay App",
            content: [
              "1. Tap the 'Pay with GoPay' button above",
              "2. You'll be redirected to the Gojek or GoPay app",
              "3. Log in if prompted",
              "4. Check the payment details (amount and merchant)",
              "5. Choose a payment method:",
              "   - GoPay Balance",
              "   - GoPay Later (PayLater)",
              "   - GoPay Coins",
              "6. Enter your GoPay PIN",
              "7. Wait for payment confirmation",
              "8. You'll automatically return to this page",
              "9. The payment status will update automatically",
            ],
          },
          {
            id: "qris",
            title: "Scan QR Code",
            content: [
              "1. If you're on a computer/laptop, the QR code will be shown",
              "2. Open Gojek, GoPay, or another e-wallet app that supports QRIS",
              "3. Select the 'Pay' or 'Scan' menu",
              "4. Point your camera at the QR code shown on screen",
              "5. Check the payment details",
              "6. Enter your PIN to confirm",
              "7. Wait for payment confirmation",
              "8. The payment status will update automatically",
            ],
          },
        ],
      },
    },
    paymentInstructionOffline: {
      paymentCode: "Payment Code",
      payToMerchant: "Pay to Merchant",
      showToCashier: "Show to the cashier",
      howToPay: "How to pay",
      indomaretInstructions: [
        "Select payment via INDOMARET",
        'Note or print the "Payment Code"',
        'Take the "Payment Code" to the store (Indomaret/Indogrosir/Superindo)',
        'Inform the cashier that the payment is made using "LINKITA"',
        'Mention the "Payment Code" to the cashier',
        'Cashier will input the "Payment Code"',
        "Pay according to the amount stated",
        "Pick up your payment receipt",
        "Payment complete",
      ],
      alfamartInstructions: [
        "Select payment via ALFA GROUP",
        'Note or print the "Payment Code"',
        'Take the "Payment Code" to the store (Alfamart/Alfamidi/Lawson/DAN+DAN)',
        'Inform the cashier that the payment is made using "LINKITA"',
        'Mention the "Payment Code" to the cashier',
        'Cashier will input the "Payment Code"',
        "Pay according to the amount stated",
        "Pick up your payment receipt",
        "Payment complete",
      ],
      genericInstruction1: "Go to the nearest {{storeName}} and show your payment code/barcode to the cashier.",
      genericInstruction2: "The cashier will confirm your transaction details.",
      genericInstruction3: "Confirm your payment with the cashier.",
      genericInstruction4: "Once your transaction is successful, you'll receive a confirmation email.",
      genericInstruction5: "Please keep your {{storeName}} receipt in case you need further assistance.",
    },
    paymentInstructionCard: {
      intro: "Enter your credit/debit card details to continue the payment.",
      challengeHandoffIntro: "Your card has been submitted. Please complete verification or wait for payment confirmation.",
      cardDetails: "Card Details",
      cardHolder: "Card Holder",
      expires: "Expires",
      cardNumberLabel: "Card Number",
      expiryLabel: "Expiry",
      cvvLabel: "CVV",
      cardholderNameLabel: "Cardholder Name",
      emailAndBillingAddress: "Email & Billing Address",
      optional: "(optional)",
      emailLabel: "Email",
      emailHelper: "Email for payment confirmation",
      billingAddressLabel: "Billing Address",
      cityLabel: "City",
      stateLabel: "State / Province",
      postalCodeLabel: "Postal code",
      secureNotice: "Your payment info is encrypted and secure",
      payNow: "Pay now",
      confirmingPayment: "Confirming payment…",
      howToPay: "How to Pay",
      instructions: [
        "1. Enter your credit/debit card number",
        "2. Enter the card's expiry date (MM/YY)",
        "3. Enter the CVV (3 or 4 digits on the back of the card)",
        "4. Enter the name as shown on the card",
        "5. Enter your email to receive payment confirmation",
        "6. Click pay and follow the verification steps if prompted",
      ],
      threeDsTitle: "Complete verification",
      threeDsFooter: "Complete the verification in the window above. Do not close this dialog until finished.",
      threeDsLoading: "Loading secure verification…",
      threeDsIframeTitle: "3DS verification",
      completeBankVerification: "Complete bank verification",
      processingCardPayment: "Processing card payment…",
      noNeedReenterCard: "You do not need to enter your card details again.",
      verificationCompletedConfirming: "Verification completed. Confirming your payment…",
      continueVerification: "Continue verification",
      cardHolderPlaceholder: "YOUR NAME",
      hideCvv: "Hide CVV",
      showCvv: "Show CVV",
      placeholderStreetAddress: "Street address",
      placeholderCity: "City",
      placeholderState: "State or province",
      placeholderPostalCode: "Postal code",
      errorCardNotSupported: "We only accept Visa, Mastercard, JCB, and American Express",
      errorInvalidCardNumber: "Invalid card number",
      errorInvalidExpiry: "Invalid",
      errorCvvDigits4: "4 digits",
      errorCvvDigits3: "3 digits",
      errorNameTooShort: "Name too short",
      errorInvalidEmail: "Invalid email",
      errorRequired: "Required",
      errorInvalidPostalCode: "Invalid postal code",
      error3dsFailed: "3DS verification failed. Please enter your card details again.",
      errorPaymentFailed: "Payment failed.",
      errorPaymentRequestFailed: "Payment request failed.",
      errorFailedAfterVerification: "Payment failed after verification. Please try again or use another card.",
      errorStillConfirming: "Payment is still being confirmed. Please tap Check Status in a moment.",
    },
    paymentInstructionLayout: {
      reffNo: "Reff No.",
      method: "Method:",
      payWithin: "Pay within",
      checking: "Checking...",
      checkTransactionStatus: "Check Transaction Status",
    },
    ovoPhoneModal: {
      title: "OVO Payment",
      phoneLabel: "Phone Number (OVO)",
      phonePlaceholder: "08xxxxxxxxxx",
      helper: "Enter your registered OVO phone number",
      payNow: "Pay Now",
      errorRequired: "Phone number is required",
      errorMustStart08: "Phone number must start with 08",
      errorMin10: "Phone number must be at least 10 digits",
      errorMax15: "Phone number must be at most 15 digits",
    },
    ovoWaitingPage: {
      errorCode14: "Phone number is invalid or not registered with OVO",
      errorCode17: "Payment declined by the user",
      errorCode26: "Failed to send notification to OVO",
      errorCode40: "Payment failed",
      errorCode54: "Transaction has expired",
      errorDefault: "Payment failed. Please try again.",
      statusNotifTitle: "Transaction Status",
      paymentInProgress: "Payment is still in progress. Please wait a moment.",
      errorTitle: "Error",
      errorCheckStatus: "Failed to check transaction status.",
      method: "Method:",
      waitingTitle: "Waiting for OVO Payment",
      sentNotification: "We've sent a notification to your OVO app",
      atNumber: "at",
      completeWithin: "Please open the OVO app and complete payment within",
      processing: "Processing...",
      notReceivedTitle: "Haven't received a notification?",
      notReceivedBody:
        "Make sure your phone number is correct and registered with OVO. Also make sure the OVO app is installed and connected to the internet.",
      failedTitle: "Payment Failed",
      tryAgain: "Try Again",
      timeoutTitle: "Waiting for Payment Confirmation",
      timeoutBody: "Your payment is being processed. Please check again in a moment.",
      checkStatus: "Check Status",
      checking: "Checking...",
    },
    redirectCountdown: {
      willRedirect: "You will be redirected in",
      seconds: "seconds...",
      goBack: "Go Back",
    },
    paymentAmountCard: {
      amountToPay: "Amount to Pay",
      details: "Details",
      customerInformation: "Customer Information",
      description: "Description",
      subTotal: "Total",
    },
  },
  id: {
    languageNames: {
      en: "English",
      id: "Bahasa Indonesia",
      zh: "中文",
    },
    layout: {
      payBefore: "Bayar sebelum",
      dateAt: "pukul",
      back: "Kembali",
      poweredBy: "Didukung oleh",
      poweredBySuffix: "",
    },
    common: {
      name: "Nama",
      phoneNumber: "Nomor Telepon",
      email: "Email",
      checkPaymentStatus: "Cek Status Pembayaran",
      processing: "Memproses...",
      close: "Tutup",
    },
    paymentMethod: {
      title: "Pilih Metode Pembayaran",
      categoryVa: "Virtual Account",
      categoryCard: "Kartu Kredit / Debit",
      categoryEwallet: "E-Wallet",
      categoryQris: "QRIS",
      categoryOfflineStore: "Gerai Retail",
      categoryDirectDebit: "Direct Debit",
      qrisSupportNote: "Mendukung QRIS dari bank besar dan e-wallet populer",
      generatingQris: "Membuat QRIS...",
      qrisNotAvailable: "QRIS belum tersedia.",
      downloadQris: "Unduh QRIS",
      cardNumberLabel: "Nomor kartu",
      cardExpiryLabel: "Tanggal kadaluarsa",
      cvvLabel: "CVV",
      nameLabel: "Nama",
      emailLabel: "Email",
      billingAddressLabel: "Alamat Penagihan",
      payNow: "Bayar Sekarang",
      errorCardNumberInvalid: "Masukkan nomor kartu yang valid.",
      errorCardNotSupported: "Kami hanya menerima Visa, Mastercard, JCB, dan American Express.",
      errorExpiryInvalid: "Masukkan tanggal kadaluarsa yang valid.",
      errorCvvDigits4: "Masukkan 4 digit CVV.",
      errorCvvDigits3: "Masukkan 3 digit CVV.",
      errorCardHolderRequired: "Masukkan nama pemegang kartu.",
      errorEmailInvalid: "Masukkan alamat email yang valid.",
    },
    paymentDetail: {
      yourDetails: "Detail Anda",
      inputName: "Masukkan nama",
      inputPhoneNumber: "Masukkan nomor telepon",
      inputEmail: "Masukkan email",
      continue: "Lanjutkan",
      paymentDetailTitle: "Detail Pembayaran",
      to: "Kepada",
      items: "Item",
      totalAmount: "Total Pembayaran",
      customerDetail: "Detail Pelanggan",
      next: "Selanjutnya",
    },
    customerDetailForm: {
      fullName: "Nama Lengkap",
      phone: "Telepon",
      johnDoePlaceholder: "john doe",
      phonePlaceholder: "08xxxxxxxxxx",
      phoneHelper: "Nomor ini akan menerima notifikasi invoice dan pembayaran",
      emailPlaceholder: "johndoe@gmail.com",
      emailHelper: "Email ini akan menerima notifikasi invoice dan pembayaran",
    },
    secondForm: {
      loadingMethods: "Memuat metode pembayaran...",
      processingPayment: "Memproses pembayaran...",
      preparingSession: "Menyiapkan sesi pembayaran...",
      allowNotifications: "Mohon izinkan notifikasi dan muat ulang halaman ini.",
      sessionNotReady: "Sesi pembayaran belum siap.",
      failedLoadMethods: "Gagal memuat metode pembayaran",
      notifErrorTitle: "Gagal Memproses Pembayaran",
      notifErrorGenericMessage: "Terjadi kesalahan saat memproses pembayaran. Silakan coba lagi.",
      preparingSessionNotifTitle: "Menyiapkan Sesi Pembayaran",
      preparingSessionNotifMessage: "Mohon tunggu hingga sesi pembayaran siap.",
      statusNotifTitle: "Status Transaksi",
      paymentReceivedMessage: "Pembayaran telah diterima",
      statusIsPrefix: "Status transaksi ini adalah",
      paymentNotifTitle: "Notifikasi Pembayaran",
      failedCardSession: "Gagal membuat sesi pembayaran kartu.",
      cardPaymentFailedFallback: "Pembayaran kartu gagal.",
      threeDsChallengeMissing: "Verifikasi 3DS diperlukan tetapi tantangan tidak dikembalikan. Silakan coba lagi.",
      creditDebitCard: "Kartu Kredit / Debit",
    },
    thirdForm: {
      loading: "Memuat...",
      statusNotifTitle: "Status Transaksi",
      paymentReceivedMessage: "Pembayaran telah diterima",
      statusIsPrefix: "Status transaksi ini adalah",
      paymentNotifTitle: "Notifikasi Pembayaran",
      errorTitle: "Error",
      errorCheckStatus: "Gagal mengecek status transaksi.",
      unknownMethod: "Metode pembayaran tidak dikenali",
    },
    errorTransactionNotFound: {
      fulfilledTitle: "Payment Link Telah Lunas Dibayar",
      fulfilledMessage:
        "Seluruh cicilan pada payment link ini telah berhasil dibayar lunas. Tidak ada tindakan lebih lanjut yang diperlukan dari Anda.",
      expiredTitle: "Payment Link Kedaluwarsa",
      expiredMessage:
        "Payment link ini telah kedaluwarsa dan tidak dapat lagi menerima pembayaran. Jika Anda masih perlu menyelesaikan pembayaran ini, silakan hubungi merchant untuk mendapatkan link baru.",
      paidTitle: "Pembayaran Sudah Selesai",
      paidMessage: "Pembayaran ini sudah berhasil diselesaikan. Tidak ada tindakan lebih lanjut yang diperlukan.",
      sessionLostTitle: "Sesi Berakhir",
      sessionLostMessage: "Sesi Anda telah berakhir. Silakan muat ulang halaman atau buka kembali payment link untuk memulai sesi baru.",
      defaultTitle: "Transaksi Tidak Ditemukan",
      defaultMessage: "Kami tidak dapat menemukan transaksi yang Anda cari. Mungkin sudah ditutup, dilunasi, atau kedaluwarsa.",
    },
    paymentSuccess: {
      title: "Pembayaran Berhasil",
      orderPaidPrefix: "Pesanan Anda #",
      orderPaidSuffix: "telah berhasil dibayar",
      amountPaid: "Jumlah Dibayar",
      datePaid: "Tanggal Bayar",
      paymentMethod: "Metode Pembayaran",
    },
    paymentLinkExpired: {
      title: "Payment Link Kedaluwarsa",
      message:
        "Payment link ini telah kedaluwarsa dan tidak dapat lagi menerima pembayaran. Jika Anda masih perlu menyelesaikan pembayaran ini, silakan hubungi merchant untuk mendapatkan link baru",
    },
    paymentInstructionVA: {
      vaNumber: "Nomor Virtual Account",
      vaName: "Nama Virtual Account",
      instructionsSuffix: "Cara Pembayaran",
      yourVaNumberFallback: "Nomor Virtual Account Anda",
    },
    paymentInstructionQRIS: {
      pleaseScan: "Silakan scan",
      howToPay: "Cara Pembayaran",
      stepByStep: "Langkah-langkah Pembayaran",
      instructions: [
        "Scan atau screenshot QR code",
        "Buka aplikasi bank/dompet digital (Gojek, OVO, Dana, QRIS BCA, dll).",
        "Pilih 'Pay' atau 'Scan'",
        "Upload tangkapan layar (hasil screenshot) QR Code.",
        "Masukkan kode PIN dompet digitalmu.",
        "Jika pembayaran telah selesai dan berhasil, kamu akan mendapat notifikasi.",
      ],
    },
    paymentInstructionEwallet: {
      payWith: "Bayar dengan",
      sections: {
        EWALLET_SHOPEEPAY: [
          {
            id: "app",
            title: "Instruksi Aplikasi Shopee",
            content: [
              "Tekan Tombol Bayar - Pilih 'Bayar dengan ShopeePay' di atas.",
              "Buka Aplikasi - Anda akan otomatis diarahkan ke aplikasi Shopee.",
              "Verifikasi Pesanan - Periksa total tagihan dan nama merchant.",
              "Pilih Metode - Gunakan Saldo, SPayLater, atau SeaBank Direct Debit.",
              "Konfirmasi PIN - Masukkan PIN ShopeePay Anda untuk menyelesaikan.",
              "Selesai - Status pembayaran akan otomatis diperbarui saat Anda kembali ke halaman ini.",
            ],
          },
          {
            id: "web",
            title: "Instruksi Website ShopeePay",
            content: [
              "Tekan Tombol Bayar - Pilih 'Bayar dengan ShopeePay' di atas.",
              "Buka halaman ShopeePay di browser Anda.",
              "Verifikasi Pesanan - Periksa total tagihan dan nama merchant.",
              "Pilih metode pembayaran ShopeePay yang tersedia.",
              "Konfirmasi PIN - Masukkan PIN ShopeePay Anda untuk menyelesaikan.",
              "Selesai - Status pembayaran akan otomatis diperbarui saat Anda kembali ke halaman ini.",
            ],
          },
        ],
        EWALLET_DANA: [
          {
            id: "app",
            title: "Aplikasi Dana",
            content: [
              "1. Klik tombol 'Bayar dengan Dana' di atas",
              "2. Anda akan diarahkan ke aplikasi Dana",
              "3. Login dengan nomor HP jika diminta",
              "4. Periksa detail pembayaran (jumlah dan merchant)",
              "5. Pilih metode pembayaran yang tersedia di Dana:",
              "   - Saldo Dana",
              "   - Kartu Debit/Kredit (jika tersedia)",
              "   - Metode pembayaran lainnya",
              "6. Masukkan PIN Dana 6 digit Anda",
              "7. Tunggu konfirmasi pembayaran berhasil",
              "8. Anda akan otomatis kembali ke halaman ini",
              "9. Status pembayaran akan diperbarui secara otomatis",
            ],
          },
          {
            id: "web",
            title: "Website Dana",
            content: [
              "1. Klik tombol 'Bayar dengan Dana' di atas",
              "2. Anda akan diarahkan ke website Dana",
              "3. Login dengan nomor HP dan OTP",
              "4. Periksa detail pembayaran",
              "5. Pilih metode pembayaran yang tersedia",
              "6. Masukkan PIN Dana",
              "7. Tunggu konfirmasi pembayaran berhasil",
              "8. Kembali ke halaman ini untuk melihat status",
            ],
          },
        ],
        EWALLET_GOPAY: [
          {
            id: "app",
            title: "Aplikasi Gojek / GoPay",
            content: [
              "1. Klik tombol 'Bayar dengan GoPay' di atas",
              "2. Anda akan diarahkan ke aplikasi Gojek atau GoPay",
              "3. Login jika diminta",
              "4. Periksa detail pembayaran (jumlah dan merchant)",
              "5. Pilih metode pembayaran:",
              "   - Saldo GoPay",
              "   - GoPay Later (PayLater)",
              "   - GoPay Coins",
              "6. Masukkan PIN GoPay Anda",
              "7. Tunggu konfirmasi pembayaran berhasil",
              "8. Anda akan otomatis kembali ke halaman ini",
              "9. Status pembayaran akan diperbarui secara otomatis",
            ],
          },
          {
            id: "qris",
            title: "Scan QR Code",
            content: [
              "1. Jika Anda membuka di komputer/laptop, QR Code akan ditampilkan",
              "2. Buka aplikasi Gojek, GoPay, atau aplikasi e-wallet lain yang mendukung QRIS",
              "3. Pilih menu 'Bayar' atau 'Scan'",
              "4. Arahkan kamera ke QR Code yang tampil di layar",
              "5. Periksa detail pembayaran",
              "6. Masukkan PIN untuk konfirmasi",
              "7. Tunggu konfirmasi pembayaran berhasil",
              "8. Status pembayaran akan diperbarui secara otomatis",
            ],
          },
        ],
      },
    },
    paymentInstructionOffline: {
      paymentCode: "Kode Pembayaran",
      payToMerchant: "Bayar ke Merchant",
      showToCashier: "Tunjukkan ke kasir",
      howToPay: "Cara pembayaran",
      indomaretInstructions: [
        "Pilih pembayaran via INDOMARET",
        'Catat atau cetak "Kode Pembayaran"',
        'Bawa "Kode Pembayaran" ke toko (Indomaret/Indogrosir/Superindo)',
        'Beritahu kasir bahwa pembayaran dilakukan menggunakan "LINKITA"',
        'Sebutkan "Kode Pembayaran" ke kasir',
        'Kasir akan memasukkan "Kode Pembayaran"',
        "Bayar sesuai jumlah yang tertera",
        "Ambil struk pembayaran Anda",
        "Pembayaran selesai",
      ],
      alfamartInstructions: [
        "Pilih pembayaran via ALFA GROUP",
        'Catat atau cetak "Kode Pembayaran"',
        'Bawa "Kode Pembayaran" ke toko (Alfamart/Alfamidi/Lawson/DAN+DAN)',
        'Beritahu kasir bahwa pembayaran dilakukan menggunakan "LINKITA"',
        'Sebutkan "Kode Pembayaran" ke kasir',
        'Kasir akan memasukkan "Kode Pembayaran"',
        "Bayar sesuai jumlah yang tertera",
        "Ambil struk pembayaran Anda",
        "Pembayaran selesai",
      ],
      genericInstruction1: "Kunjungi {{storeName}} terdekat dan tunjukkan kode pembayaran/barcode Anda ke kasir.",
      genericInstruction2: "Kasir akan mengonfirmasi detail transaksi Anda.",
      genericInstruction3: "Konfirmasi pembayaran Anda dengan kasir.",
      genericInstruction4: "Setelah transaksi berhasil, Anda akan menerima email konfirmasi.",
      genericInstruction5: "Simpan struk {{storeName}} Anda jika sewaktu-waktu memerlukan bantuan lebih lanjut.",
    },
    paymentInstructionCard: {
      intro: "Masukkan detail kartu kredit/debit Anda untuk melanjutkan pembayaran.",
      challengeHandoffIntro: "Kartu Anda sudah dikirim. Selesaikan verifikasi atau tunggu konfirmasi pembayaran.",
      cardDetails: "Detail Kartu",
      cardHolder: "Pemegang Kartu",
      expires: "Kadaluarsa",
      cardNumberLabel: "Nomor Kartu",
      expiryLabel: "Kadaluarsa",
      cvvLabel: "CVV",
      cardholderNameLabel: "Nama Pemegang Kartu",
      emailAndBillingAddress: "Email & Alamat Penagihan",
      optional: "(opsional)",
      emailLabel: "Email",
      emailHelper: "Email untuk konfirmasi pembayaran",
      billingAddressLabel: "Alamat Penagihan",
      cityLabel: "Kota",
      stateLabel: "Provinsi",
      postalCodeLabel: "Kode pos",
      secureNotice: "Informasi pembayaran Anda dienkripsi dan aman",
      payNow: "Bayar sekarang",
      confirmingPayment: "Mengonfirmasi pembayaran…",
      howToPay: "Cara Pembayaran",
      instructions: [
        "1. Masukkan nomor kartu kredit/debit Anda",
        "2. Masukkan tanggal kadaluarsa kartu (MM/YY)",
        "3. Masukkan CVV (3 atau 4 digit di belakang kartu)",
        "4. Masukkan nama sesuai yang tertera di kartu",
        "5. Masukkan email untuk menerima konfirmasi pembayaran",
        "6. Klik tombol bayar dan ikuti langkah verifikasi jika diminta",
      ],
      threeDsTitle: "Selesaikan verifikasi",
      threeDsFooter: "Selesaikan verifikasi pada jendela di atas. Jangan tutup dialog ini sebelum selesai.",
      threeDsLoading: "Memuat verifikasi aman…",
      threeDsIframeTitle: "Verifikasi 3DS",
      completeBankVerification: "Selesaikan verifikasi bank",
      processingCardPayment: "Memproses pembayaran kartu…",
      noNeedReenterCard: "Anda tidak perlu memasukkan detail kartu lagi.",
      verificationCompletedConfirming: "Verifikasi selesai. Mengonfirmasi pembayaran Anda…",
      continueVerification: "Lanjutkan verifikasi",
      cardHolderPlaceholder: "NAMA ANDA",
      hideCvv: "Sembunyikan CVV",
      showCvv: "Tampilkan CVV",
      placeholderStreetAddress: "Alamat jalan",
      placeholderCity: "Kota",
      placeholderState: "Provinsi",
      placeholderPostalCode: "Kode pos",
      errorCardNotSupported: "Kami hanya menerima Visa, Mastercard, JCB, dan American Express",
      errorInvalidCardNumber: "Nomor kartu tidak valid",
      errorInvalidExpiry: "Tidak valid",
      errorCvvDigits4: "4 digit",
      errorCvvDigits3: "3 digit",
      errorNameTooShort: "Nama terlalu pendek",
      errorInvalidEmail: "Email tidak valid",
      errorRequired: "Wajib diisi",
      errorInvalidPostalCode: "Kode pos tidak valid",
      error3dsFailed: "Verifikasi 3DS gagal. Silakan masukkan kembali detail kartu Anda.",
      errorPaymentFailed: "Pembayaran gagal.",
      errorPaymentRequestFailed: "Permintaan pembayaran gagal.",
      errorFailedAfterVerification: "Pembayaran gagal setelah verifikasi. Silakan coba lagi atau gunakan kartu lain.",
      errorStillConfirming: "Pembayaran masih dikonfirmasi. Silakan ketuk Cek Status sebentar lagi.",
    },
    paymentInstructionLayout: {
      reffNo: "No. Ref",
      method: "Metode:",
      payWithin: "Bayar dalam",
      checking: "Memeriksa...",
      checkTransactionStatus: "Cek Status Transaksi",
    },
    ovoPhoneModal: {
      title: "Pembayaran OVO",
      phoneLabel: "Nomor HP (OVO)",
      phonePlaceholder: "08xxxxxxxxxx",
      helper: "Masukkan nomor HP yang terdaftar OVO",
      payNow: "Bayar Sekarang",
      errorRequired: "Nomor HP wajib diisi",
      errorMustStart08: "Nomor HP harus diawali dengan 08",
      errorMin10: "Nomor HP minimal 10 digit",
      errorMax15: "Nomor HP maksimal 15 digit",
    },
    ovoWaitingPage: {
      errorCode14: "Nomor HP tidak valid atau tidak terdaftar di OVO",
      errorCode17: "Pembayaran ditolak oleh pengguna",
      errorCode26: "Gagal mengirim notifikasi ke OVO",
      errorCode40: "Pembayaran gagal",
      errorCode54: "Transaksi telah kadaluarsa",
      errorDefault: "Pembayaran gagal. Silakan coba lagi.",
      statusNotifTitle: "Status Transaksi",
      paymentInProgress: "Pembayaran masih dalam proses. Silakan tunggu beberapa saat.",
      errorTitle: "Error",
      errorCheckStatus: "Gagal mengecek status transaksi.",
      method: "Metode:",
      waitingTitle: "Menunggu Pembayaran OVO",
      sentNotification: "Kami sudah mengirim notifikasi ke aplikasi OVO Anda",
      atNumber: "di nomor",
      completeWithin: "Silakan buka aplikasi OVO dan selesaikan pembayaran dalam",
      processing: "Memproses...",
      notReceivedTitle: "Belum menerima notifikasi?",
      notReceivedBody:
        "Pastikan nomor HP sudah benar dan terdaftar di OVO. Pastikan juga aplikasi OVO sudah terinstall dan terhubung ke internet.",
      failedTitle: "Pembayaran Gagal",
      tryAgain: "Coba Lagi",
      timeoutTitle: "Menunggu Konfirmasi Pembayaran",
      timeoutBody: "Pembayaran Anda sedang diproses. Silakan periksa kembali beberapa saat lagi.",
      checkStatus: "Periksa Status",
      checking: "Memeriksa...",
    },
    redirectCountdown: {
      willRedirect: "Anda akan dialihkan dalam",
      seconds: "detik...",
      goBack: "Kembali",
    },
    paymentAmountCard: {
      amountToPay: "Jumlah yang Dibayar",
      details: "Detail",
      customerInformation: "Informasi Pelanggan",
      description: "Deskripsi",
      subTotal: "Total",
    },
  },
  zh: {
    languageNames: {
      en: "English",
      id: "Bahasa Indonesia",
      zh: "中文",
    },
    layout: {
      payBefore: "请在此前付款",
      dateAt: "于",
      back: "返回",
      poweredBy: "由",
      poweredBySuffix: "支持",
    },
    common: {
      name: "姓名",
      phoneNumber: "电话号码",
      email: "电子邮箱",
      checkPaymentStatus: "查询付款状态",
      processing: "处理中...",
      close: "关闭",
    },
    paymentMethod: {
      title: "选择支付方式",
      categoryVa: "虚拟账户",
      categoryCard: "信用卡/借记卡",
      categoryEwallet: "电子钱包",
      categoryQris: "QRIS 扫码支付",
      categoryOfflineStore: "零售门店",
      categoryDirectDebit: "直接借记",
      qrisSupportNote: "支持各大银行及热门电子钱包的 QRIS 扫码支付",
      generatingQris: "正在生成 QRIS...",
      qrisNotAvailable: "QRIS 暂不可用。",
      downloadQris: "下载 QRIS",
      cardNumberLabel: "卡号",
      cardExpiryLabel: "有效期",
      cvvLabel: "安全码 (CVV)",
      nameLabel: "姓名",
      emailLabel: "电子邮箱",
      billingAddressLabel: "账单地址",
      payNow: "立即支付",
      errorCardNumberInvalid: "请输入有效的卡号。",
      errorCardNotSupported: "我们仅支持 Visa、Mastercard、JCB 和 American Express。",
      errorExpiryInvalid: "请输入有效的有效期。",
      errorCvvDigits4: "请输入 4 位安全码。",
      errorCvvDigits3: "请输入 3 位安全码。",
      errorCardHolderRequired: "请输入持卡人姓名。",
      errorEmailInvalid: "请输入有效的电子邮箱地址。",
    },
    paymentDetail: {
      yourDetails: "您的信息",
      inputName: "请输入姓名",
      inputPhoneNumber: "请输入电话号码",
      inputEmail: "请输入电子邮箱",
      continue: "继续",
      paymentDetailTitle: "付款详情",
      to: "收款方",
      items: "商品",
      totalAmount: "总金额",
      customerDetail: "客户信息",
      next: "下一步",
    },
    customerDetailForm: {
      fullName: "姓名",
      phone: "电话",
      johnDoePlaceholder: "张三",
      phonePlaceholder: "08xxxxxxxxxx",
      phoneHelper: "该号码将接收账单与付款通知",
      emailPlaceholder: "johndoe@gmail.com",
      emailHelper: "该邮箱将接收账单与付款通知",
    },
    secondForm: {
      loadingMethods: "正在加载支付方式...",
      processingPayment: "正在处理付款...",
      preparingSession: "正在准备付款会话...",
      allowNotifications: "请允许通知权限并刷新此页面。",
      sessionNotReady: "付款会话尚未准备就绪。",
      failedLoadMethods: "加载支付方式失败",
      notifErrorTitle: "付款处理失败",
      notifErrorGenericMessage: "处理付款时发生错误，请重试。",
      preparingSessionNotifTitle: "正在准备付款会话",
      preparingSessionNotifMessage: "请稍候，直到付款会话准备就绪。",
      statusNotifTitle: "交易状态",
      paymentReceivedMessage: "已收到付款",
      statusIsPrefix: "此交易状态为",
      paymentNotifTitle: "付款通知",
      failedCardSession: "创建银行卡付款会话失败。",
      cardPaymentFailedFallback: "银行卡付款失败。",
      threeDsChallengeMissing: "需要 3DS 验证，但未返回验证内容。请重试。",
      creditDebitCard: "信用卡 / 借记卡",
    },
    thirdForm: {
      loading: "加载中...",
      statusNotifTitle: "交易状态",
      paymentReceivedMessage: "已收到付款",
      statusIsPrefix: "此交易状态为",
      paymentNotifTitle: "付款通知",
      errorTitle: "错误",
      errorCheckStatus: "查询交易状态失败。",
      unknownMethod: "无法识别的支付方式",
    },
    errorTransactionNotFound: {
      fulfilledTitle: "付款链接已全额付清",
      fulfilledMessage: "此付款链接的所有分期款项均已成功付清，您无需进行任何操作。",
      expiredTitle: "付款链接已过期",
      expiredMessage: "此付款链接已过期，无法再接受付款。如仍需完成此笔付款，请联系商户获取新的链接。",
      paidTitle: "付款已完成",
      paidMessage: "此付款已成功完成，您无需进行任何操作。",
      sessionLostTitle: "会话已过期",
      sessionLostMessage: "您的会话已过期，请重新加载页面或重新打开付款链接以开始新的会话。",
      defaultTitle: "未找到交易",
      defaultMessage: "我们找不到您要查找的交易，该交易可能已关闭、已付清或已过期。",
    },
    paymentSuccess: {
      title: "支付成功",
      orderPaidPrefix: "您的订单 #",
      orderPaidSuffix: "已成功付款",
      amountPaid: "已付金额",
      datePaid: "付款日期",
      paymentMethod: "支付方式",
    },
    paymentLinkExpired: {
      title: "付款链接已过期",
      message: "此付款链接已过期，无法再接受付款。如仍需完成此笔付款，请联系商户获取新的链接",
    },
    paymentInstructionVA: {
      vaNumber: "虚拟账户号码",
      vaName: "虚拟账户名称",
      instructionsSuffix: "支付说明",
      yourVaNumberFallback: "您的虚拟账户号码",
    },
    paymentInstructionQRIS: {
      pleaseScan: "请扫描",
      howToPay: "支付方式",
      stepByStep: "分步操作说明",
      instructions: [
        "扫描或截图二维码",
        "打开您的银行/电子钱包应用（Gojek、OVO、Dana、QRIS BCA 等）。",
        "选择「支付」或「扫描」",
        "上传二维码截图。",
        "输入您的电子钱包 PIN 码。",
        "付款完成后，您将收到通知。",
      ],
    },
    paymentInstructionEwallet: {
      payWith: "使用以下方式付款：",
      sections: {
        EWALLET_SHOPEEPAY: [
          {
            id: "app",
            title: "Shopee App 操作说明",
            content: [
              "点击支付按钮 - 在上方选择「使用 ShopeePay 付款」。",
              "打开应用 - 系统将自动跳转至 Shopee 应用。",
              "核对订单 - 检查总金额及商户名称。",
              "选择支付方式 - 使用余额、SPayLater 或 SeaBank 直接借记。",
              "确认 PIN - 输入您的 ShopeePay PIN 码以完成支付。",
              "完成 - 返回此页面后，付款状态将自动更新。",
            ],
          },
          {
            id: "web",
            title: "ShopeePay 网页版操作说明",
            content: [
              "点击支付按钮 - 在上方选择「使用 ShopeePay 付款」。",
              "在浏览器中打开 ShopeePay 页面。",
              "核对订单 - 检查总金额及商户名称。",
              "选择可用的 ShopeePay 支付方式。",
              "确认 PIN - 输入您的 ShopeePay PIN 码以完成支付。",
              "完成 - 返回此页面后，付款状态将自动更新。",
            ],
          },
        ],
        EWALLET_DANA: [
          {
            id: "app",
            title: "Dana 应用",
            content: [
              "1. 点击上方「使用 Dana 付款」按钮",
              "2. 系统将跳转至 Dana 应用",
              "3. 如有提示，请使用手机号码登录",
              "4. 核对付款详情（金额及商户）",
              "5. 选择 Dana 中可用的支付方式：",
              "   - Dana 余额",
              "   - 借记卡/信用卡（如可用）",
              "   - 其他支付方式",
              "6. 输入您的 6 位 Dana PIN 码",
              "7. 等待付款确认",
              "8. 系统将自动返回此页面",
              "9. 付款状态将自动更新",
            ],
          },
          {
            id: "web",
            title: "Dana 网页版",
            content: [
              "1. 点击上方「使用 Dana 付款」按钮",
              "2. 系统将跳转至 Dana 网站",
              "3. 使用手机号码及验证码登录",
              "4. 核对付款详情",
              "5. 选择可用的支付方式",
              "6. 输入 Dana PIN 码",
              "7. 等待付款确认",
              "8. 返回此页面查看状态",
            ],
          },
        ],
        EWALLET_GOPAY: [
          {
            id: "app",
            title: "Gojek / GoPay 应用",
            content: [
              "1. 点击上方「使用 GoPay 付款」按钮",
              "2. 系统将跳转至 Gojek 或 GoPay 应用",
              "3. 如有提示，请登录",
              "4. 核对付款详情（金额及商户）",
              "5. 选择支付方式：",
              "   - GoPay 余额",
              "   - GoPay Later（先享后付）",
              "   - GoPay Coins",
              "6. 输入您的 GoPay PIN 码",
              "7. 等待付款确认",
              "8. 系统将自动返回此页面",
              "9. 付款状态将自动更新",
            ],
          },
          {
            id: "qris",
            title: "扫描二维码",
            content: [
              "1. 如果您在电脑/笔记本上打开，将显示二维码",
              "2. 打开 Gojek、GoPay 或其他支持 QRIS 的电子钱包应用",
              "3. 选择「支付」或「扫描」菜单",
              "4. 将摄像头对准屏幕上显示的二维码",
              "5. 核对付款详情",
              "6. 输入 PIN 码进行确认",
              "7. 等待付款确认",
              "8. 付款状态将自动更新",
            ],
          },
        ],
      },
    },
    paymentInstructionOffline: {
      paymentCode: "付款代码",
      payToMerchant: "付款给商户",
      showToCashier: "出示给收银员",
      howToPay: "付款方式",
      indomaretInstructions: [
        "选择通过 INDOMARET 付款",
        "记下或打印「付款代码」",
        "携带「付款代码」前往门店（Indomaret/Indogrosir/Superindo）",
        "告知收银员使用「LINKITA」付款",
        "向收银员提供「付款代码」",
        "收银员将输入「付款代码」",
        "按标示金额付款",
        "领取付款收据",
        "付款完成",
      ],
      alfamartInstructions: [
        "选择通过 ALFA GROUP 付款",
        "记下或打印「付款代码」",
        "携带「付款代码」前往门店（Alfamart/Alfamidi/Lawson/DAN+DAN）",
        "告知收银员使用「LINKITA」付款",
        "向收银员提供「付款代码」",
        "收银员将输入「付款代码」",
        "按标示金额付款",
        "领取付款收据",
        "付款完成",
      ],
      genericInstruction1: "前往最近的{{storeName}}，向收银员出示您的付款代码/条形码。",
      genericInstruction2: "收银员将核对您的交易详情。",
      genericInstruction3: "与收银员确认付款。",
      genericInstruction4: "交易成功后，您将收到确认邮件。",
      genericInstruction5: "请妥善保管您的{{storeName}}收据，以备日后需要协助时使用。",
    },
    paymentInstructionCard: {
      intro: "请输入您的信用卡/借记卡信息以继续付款。",
      challengeHandoffIntro: "您的卡片信息已提交。请完成验证或等待付款确认。",
      cardDetails: "卡片信息",
      cardHolder: "持卡人",
      expires: "有效期",
      cardNumberLabel: "卡号",
      expiryLabel: "有效期",
      cvvLabel: "安全码 (CVV)",
      cardholderNameLabel: "持卡人姓名",
      emailAndBillingAddress: "电子邮箱与账单地址",
      optional: "（选填）",
      emailLabel: "电子邮箱",
      emailHelper: "用于接收付款确认的邮箱",
      billingAddressLabel: "账单地址",
      cityLabel: "城市",
      stateLabel: "省/州",
      postalCodeLabel: "邮政编码",
      secureNotice: "您的付款信息已加密，安全可靠",
      payNow: "立即支付",
      confirmingPayment: "正在确认付款…",
      howToPay: "支付方式",
      instructions: [
        "1. 输入您的信用卡/借记卡卡号",
        "2. 输入卡片有效期 (MM/YY)",
        "3. 输入卡片背面的 3 或 4 位安全码 (CVV)",
        "4. 输入卡片上显示的姓名",
        "5. 输入电子邮箱以接收付款确认",
        "6. 点击支付按钮，并按提示完成验证步骤",
      ],
      threeDsTitle: "完成验证",
      threeDsFooter: "请在上方窗口中完成验证，完成前请勿关闭此对话框。",
      threeDsLoading: "正在加载安全验证…",
      threeDsIframeTitle: "3DS 验证",
      completeBankVerification: "完成银行验证",
      processingCardPayment: "正在处理银行卡付款…",
      noNeedReenterCard: "您无需再次输入卡片信息。",
      verificationCompletedConfirming: "验证已完成。正在确认您的付款…",
      continueVerification: "继续验证",
      cardHolderPlaceholder: "您的姓名",
      hideCvv: "隐藏安全码",
      showCvv: "显示安全码",
      placeholderStreetAddress: "街道地址",
      placeholderCity: "城市",
      placeholderState: "省/州",
      placeholderPostalCode: "邮政编码",
      errorCardNotSupported: "我们仅支持 Visa、Mastercard、JCB 和 American Express",
      errorInvalidCardNumber: "卡号无效",
      errorInvalidExpiry: "无效",
      errorCvvDigits4: "4 位数字",
      errorCvvDigits3: "3 位数字",
      errorNameTooShort: "姓名过短",
      errorInvalidEmail: "邮箱无效",
      errorRequired: "必填项",
      errorInvalidPostalCode: "邮政编码无效",
      error3dsFailed: "3DS 验证失败，请重新输入您的卡片信息。",
      errorPaymentFailed: "付款失败。",
      errorPaymentRequestFailed: "付款请求失败。",
      errorFailedAfterVerification: "验证后付款失败。请重试或使用其他卡片。",
      errorStillConfirming: "付款仍在确认中。请稍后点击查询状态。",
    },
    paymentInstructionLayout: {
      reffNo: "参考编号",
      method: "支付方式：",
      payWithin: "请在以下时间内付款",
      checking: "查询中...",
      checkTransactionStatus: "查询交易状态",
    },
    ovoPhoneModal: {
      title: "OVO 付款",
      phoneLabel: "手机号码 (OVO)",
      phonePlaceholder: "08xxxxxxxxxx",
      helper: "请输入已在 OVO 注册的手机号码",
      payNow: "立即支付",
      errorRequired: "手机号码为必填项",
      errorMustStart08: "手机号码必须以 08 开头",
      errorMin10: "手机号码至少需要 10 位数字",
      errorMax15: "手机号码最多为 15 位数字",
    },
    ovoWaitingPage: {
      errorCode14: "手机号码无效或未在 OVO 注册",
      errorCode17: "用户拒绝了此次付款",
      errorCode26: "向 OVO 发送通知失败",
      errorCode40: "付款失败",
      errorCode54: "交易已过期",
      errorDefault: "付款失败，请重试。",
      statusNotifTitle: "交易状态",
      paymentInProgress: "付款仍在处理中，请稍候。",
      errorTitle: "错误",
      errorCheckStatus: "查询交易状态失败。",
      method: "支付方式：",
      waitingTitle: "等待 OVO 付款",
      sentNotification: "我们已向您的 OVO 应用发送通知",
      atNumber: "号码为",
      completeWithin: "请打开 OVO 应用并在以下时间内完成付款",
      processing: "处理中...",
      notReceivedTitle: "尚未收到通知？",
      notReceivedBody: "请确认手机号码正确且已在 OVO 注册。同时请确认 OVO 应用已安装并已连接网络。",
      failedTitle: "付款失败",
      tryAgain: "重试",
      timeoutTitle: "等待付款确认",
      timeoutBody: "您的付款正在处理中，请稍后再次查看。",
      checkStatus: "查询状态",
      checking: "查询中...",
    },
    redirectCountdown: {
      willRedirect: "您将在",
      seconds: "秒后跳转...",
      goBack: "返回",
    },
    paymentAmountCard: {
      amountToPay: "应付金额",
      details: "详情",
      customerInformation: "客户信息",
      description: "描述",
      subTotal: "总计",
    },
  },
};

export default translations;
